import { useState } from "react";
import type { Pace, Stats } from "@shared/types.js";
import { fnum, fdate } from "../lib/format.js";

/**
 * The headline: where we are, how fast we are going, and whether that lands
 * before the deadline. This is the one number set that changes decisions.
 */
export function PaceCard({
  s, pace, targetDate, prevCompletion, onSetTarget,
}: {
  s: Stats;
  pace: Pace;
  targetDate: string | null;
  prevCompletion: number | null;
  onSetTarget: (d: string | null) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(targetDate ?? "");

  const delta = prevCompletion === null ? null : s.completion - prevCompletion;
  const tone = pace.onTrack === null ? "" : pace.onTrack ? " ok" : " behind";

  const save = () => {
    onSetTarget(draft.trim() ? draft.trim() : null);
    setEditing(false);
  };

  return (
    <div className={`hero${tone}`}>
      <div className="hero-main">
        <div className="lab">Overall completion</div>
        <div className="hero-val">{s.completion.toFixed(1)}%</div>
        <div className="hero-sub">
          {fnum(s.entered)} of {fnum(s.total)} records priced or estimated
          {delta !== null && (
            <> &middot;{" "}
              <span className={`delta ${delta > 0 ? "up" : delta < 0 ? "down" : "flat"}`}>
                {delta > 0 ? "+" : ""}{delta.toFixed(1)}% vs previous snapshot
              </span>
            </>
          )}
        </div>
      </div>

      <div className="hero-stats">
        <div className="hs">
          <div className="lab">Remaining</div>
          <div className="v">{fnum(pace.remaining)}</div>
          <div className="f">records with nothing entered</div>
        </div>

        <div className="hs">
          <div className="lab">Current pace</div>
          <div className="v">{pace.recentPerMonth.toFixed(0)}<small>/mo</small></div>
          <div className="f">
            mean of last {pace.recentMonths} active month{pace.recentMonths === 1 ? "" : "s"}
          </div>
        </div>

        <div className="hs">
          <div className="lab">Pace needed</div>
          <div className="v">
            {pace.requiredPerMonth === null
              ? "–"
              : pace.requiredPerMonth === Infinity
                ? "∞"
                : <>{pace.requiredPerMonth.toFixed(0)}<small>/mo</small></>}
          </div>
          <div className="f">
            {targetDate ? `to finish by ${fdate(targetDate)}` : "set a target date"}
          </div>
        </div>

        <div className="hs">
          <div className="lab">Projected finish</div>
          <div className="v sm">
            {pace.projectedFinish ? fdate(pace.projectedFinish) : "–"}
          </div>
          <div className="f">
            {pace.monthsToFinish === null
              ? "no entries yet, so no pace"
              : `about ${pace.monthsToFinish.toFixed(0)} months at current pace`}
          </div>
        </div>
      </div>

      <div className="hero-foot">
        {editing ? (
          <div className="rowflex">
            <label htmlFor="target" className="tag">Target date</label>
            <input
              id="target" type="date" value={draft} autoFocus
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") save(); if (e.key === "Escape") setEditing(false); }}
            />
            <button className="btn btn--sm btn--primary" type="button" onClick={save}>Save</button>
            <button className="btn btn--sm" type="button" onClick={() => setEditing(false)}>Cancel</button>
            {targetDate && (
              <button
                className="btn btn--sm" type="button"
                onClick={() => { onSetTarget(null); setDraft(""); setEditing(false); }}
              >
                Clear
              </button>
            )}
          </div>
        ) : targetDate === null ? (
          <div className="rowflex">
            <span className="muted">
              No target date set &mdash; without one, the projection has nothing to be measured against.
            </span>
            <button className="btn btn--sm btn--primary" type="button" onClick={() => setEditing(true)}>
              Set target date
            </button>
          </div>
        ) : (
          <div className="rowflex">
            <span className={`verdict${tone}`}>
              {pace.onTrack
                ? "On track"
                : `Behind: needs ${
                    pace.requiredPerMonth === Infinity
                      ? "more time"
                      : `${(pace.requiredPerMonth! - pace.recentPerMonth).toFixed(0)} more per month`
                  }`}
            </span>
            <span className="muted">
              Target {fdate(targetDate)}
              {pace.monthsToTarget !== null && ` · ${pace.monthsToTarget.toFixed(0)} months away`}
            </span>
            <button className="btn btn--sm" type="button" onClick={() => { setDraft(targetDate); setEditing(true); }}>
              Change
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
